import { createResource, onCleanup, createEffect, on } from "solid-js";
import { getProjectQuota } from "../api/projects";
import { quotaPaywall } from "../stores/quotaPaywallStore";
import type { BoardEvent, ProjectId, ProjectQuotaStatus } from "../api/types";

const REFETCH_JITTER_MS = 2000;

export interface UseProjectQuotaStatusResult {
  status: () => ProjectQuotaStatus | undefined;
  handleWebSocketEvent: (event: BoardEvent) => void;
  refetch: () => void;
}

export function useProjectQuotaStatus(
  projectId: () => ProjectId | undefined,
  jitterMs: number = REFETCH_JITTER_MS,
): UseProjectQuotaStatusResult {
  // `createResource` keyed on the project: a falsy key skips the fetch (D3).
  const [quota, { refetch: refetchResource }] = createResource(
    projectId,
    async (id) => {
      try {
        return await getProjectQuota(id);
      } catch {
        return undefined; // the banner is an enrichment; any failure renders nothing
      }
    },
  );

  // A resolved response can still be for the project we've since switched
  // away from (a resource in flight when projectId changes); only surface it
  // once it matches the project currently selected, rather than gating on
  // `quota.state === "ready"`, which would also hide it during a refetch.
  const status = () => {
    const latest = quota.latest;
    return latest && latest.project_id === projectId() ? latest : undefined;
  };

  // FLOW-945 emits a project-scoped `QuotaLevelChanged` (`cap: "project"`) to
  // this project's channel on every change of its budget level, in both
  // directions. A tenant crossing also lands here and is also refetched —
  // harmless, and cheaper than filtering. Between events the status is as
  // stale as the Settings meter; polling is rejected.
  let pending: ReturnType<typeof setTimeout> | undefined;
  const scheduleRefetch = () => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = undefined;
      void refetchResource();
    }, Math.random() * jitterMs);
  };
  onCleanup(() => clearTimeout(pending));

  const handleWebSocketEvent = (event: BoardEvent) => {
    if (event.type !== "QuotaLevelChanged") return;
    if (event.payload.project_id !== projectId()) return;
    scheduleRefetch();
  };

  const refetch = () => {
    if (!projectId()) return;
    clearTimeout(pending);
    pending = undefined;
    void refetchResource();
  };

  // D2: a PROJECT_BUDGET_EXHAUSTED refusal is the server stating that
  // used >= budget was already true when the gate read the ledger, so this
  // read is not racing the usage writer. `refetch` is a no-op with no
  // project selected, and a tenant-scoped refusal is not about this budget.
  //
  // `on(..., { defer: true })` rather than a bare `createEffect`: `on` runs
  // the handler untracked, so `refetch`'s own `projectId()` read cannot make
  // a project switch re-trigger this; and `defer` skips the run at creation,
  // so remounting the shell with a paywall already open does not duplicate
  // the fetch `createResource` makes for a fresh key.
  createEffect(
    on(
      () => quotaPaywall()?.scope === "project",
      (isProject) => {
        if (isProject) refetch();
      },
      { defer: true },
    ),
  );

  return { status, handleWebSocketEvent, refetch };
}
